import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { userManager } from '../services/oidc';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Decodifica o payload de um JWT que usa base64url (RFC 4648 §5).
 * O atob() nativo exige base64 padrão — esta função faz a conversão antes.
 */
function decodeJwtPayload(token) {
  try {
    const base64url = token.split('.')[1];
    if (!base64url) return null;
    // Converter base64url → base64 padrão
    const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
    // Adicionar padding se necessário
    const padded = base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, '=');
    return JSON.parse(atob(padded));
  } catch (e) {
    console.warn('[AuthContext] Falha ao decodificar JWT:', e.message);
    return null;
  }
}

/**
 * Verifica se o usuário OIDC possui a role 'agroverita_observability'.
 *
 * Verifica em três lugares, pois o Identity Server pode emitir o claim de formas diferentes:
 *   1. user.profile     → payload do ID token (decodificado pelo oidc-client-ts)
 *   2. access token     → decodificado manualmente com correção de base64url
 *   3. Nome longo .NET  → 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role'
 *      (emitido quando o IProfileService usa ClaimTypes.Role em vez de JwtClaimTypes.Role)
 */
function hasObservabilityRole(user) {
  const ROLE_NAME = 'agroverita_observability';
  const NET_ROLE_CLAIM = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';

  function containsRole(obj) {
    if (!obj) return false;
    const candidates = [
      obj['role'],
      obj['roles'],
      obj[NET_ROLE_CLAIM],
    ];
    for (const raw of candidates) {
      if (!raw) continue;
      const arr = Array.isArray(raw) ? raw : [raw];
      if (arr.includes(ROLE_NAME)) return true;
    }
    return false;
  }

  // 1. Verificar no ID token (user.profile — decodificado pelo oidc-client-ts)
  if (containsRole(user?.profile)) {
    console.log('[AuthContext] Role encontrada no ID token (user.profile)');
    return true;
  }

  // 2. Verificar no access token (decodificação manual com base64url corrigida)
  const accessPayload = decodeJwtPayload(user?.access_token);
  if (accessPayload) {
    if (containsRole(accessPayload)) {
      console.log('[AuthContext] Role encontrada no access token');
      return true;
    }
    // Log de diagnóstico: mostra as claims disponíveis para facilitar depuração
    console.warn(
      '[AuthContext] Role não encontrada. Claims disponíveis no access token:',
      Object.keys(accessPayload)
    );
  }

  // Log de diagnóstico: mostra claims do ID token
  if (user?.profile) {
    console.warn(
      '[AuthContext] Claims disponíveis no ID token (user.profile):',
      Object.keys(user.profile)
    );
  }

  return false;
}

// ---------------------------------------------------------------------------
// Context definition
// ---------------------------------------------------------------------------

const AuthContext = createContext(undefined);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  // Ref para evitar re-processamento do mesmo usuário em renders múltiplos
  const processedSubRef = useRef(null);

  // Ref para impedir chamadas concorrentes a signinRedirect()
  const loginInflightRef = useRef(false);

  /**
   * Recebe um usuário OIDC já autenticado, verifica a role obrigatória
   * e atualiza o estado do context.
   */
  const handleUserLoaded = useCallback((loadedUser) => {
    const sub = loadedUser?.profile?.sub;

    // Evitar reprocessar o mesmo usuário
    if (sub && processedSubRef.current === sub) {
      setLoading(false);
      return;
    }
    processedSubRef.current = sub || null;

    if (!hasObservabilityRole(loadedUser)) {
      // Usuário autenticado mas sem a role necessária
      setUser(loadedUser);
      setAccessDenied(true);
      setLoading(false);
      return;
    }

    setUser(loadedUser);
    setAccessDenied(false);
    setLoading(false);
  }, []);

  // ---------------------------------------------------------------------------
  // Inicialização: verificar se já existe sessão ativa no localStorage
  // ---------------------------------------------------------------------------
  useEffect(() => {
    // Se estamos processando o callback OIDC, aguardar sem fazer nada —
    // o AuthContext receberá o evento 'userLoaded' após o callback completar.
    const path = window.location.pathname;
    if (path === '/callback' || path === '/silent-renew') {
      setLoading(false);
      return;
    }

    userManager.getUser().then((existingUser) => {
      if (existingUser && !existingUser.expired) {
        handleUserLoaded(existingUser);
      } else if (existingUser && existingUser.expired) {
        // Sessão expirada em localStorage — limpar antes de qualquer redirect (R4)
        userManager.removeUser().finally(() => {
          processedSubRef.current = null;
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
    }).catch((error) => {
      console.error('[AuthContext] Erro ao verificar sessão existente:', error);
      setLoading(false);
    });
  }, [handleUserLoaded]);

  // ---------------------------------------------------------------------------
  // Eventos do UserManager
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const onUserLoaded = (loadedUser) => {
      handleUserLoaded(loadedUser);
    };

    const onUserUnloaded = () => {
      setUser(null);
      setAccessDenied(false);
      processedSubRef.current = null;
    };

    const onAccessTokenExpired = () => {
      const path = window.location.pathname;
      // Não disparar redirect se estamos no meio do processamento do callback —
      // isso aborta a troca code→token e causa loop.
      if (path === '/callback' || path === '/silent-renew') {
        console.warn('[AuthContext] Access token expirado durante', path, '— ignorando para não interromper o callback');
        return;
      }
      console.warn('[AuthContext] Access token expirado — limpando sessão e redirecionando para login');
      userManager.removeUser().finally(() => {
        processedSubRef.current = null;
        setUser(null);
        if (!loginInflightRef.current) {
          loginInflightRef.current = true;
          userManager.signinRedirect().catch((error) => {
            console.error('[AuthContext] Erro em signinRedirect após expiração:', error);
            loginInflightRef.current = false;
          });
        }
      });
    };

    const onSilentRenewError = (error) => {
      // Apenas loga. NÃO força redirect — o token atual ainda é válido por algum
      // tempo. Quando expirar de fato, onAccessTokenExpired tratará.
      console.error('[AuthContext] Falha no silent renew (token continuará válido até expirar):', error);
    };

    userManager.events.addUserLoaded(onUserLoaded);
    userManager.events.addUserUnloaded(onUserUnloaded);
    userManager.events.addAccessTokenExpired(onAccessTokenExpired);
    userManager.events.addSilentRenewError(onSilentRenewError);

    return () => {
      userManager.events.removeUserLoaded(onUserLoaded);
      userManager.events.removeUserUnloaded(onUserUnloaded);
      userManager.events.removeAccessTokenExpired(onAccessTokenExpired);
      userManager.events.removeSilentRenewError(onSilentRenewError);
    };
  }, [handleUserLoaded]);

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  const login = useCallback(() => {
    if (loginInflightRef.current) {
      console.debug('[AuthContext] signinRedirect já em andamento — ignorando chamada duplicada');
      return;
    }
    loginInflightRef.current = true;
    userManager.signinRedirect().catch((error) => {
      console.error('[AuthContext] Erro em signinRedirect:', error);
      loginInflightRef.current = false;
    });
  }, []);

  const logout = useCallback(() => {
    const idToken = user?.id_token;
    setLoggingOut(true);
    userManager.removeUser().then(() => {
      userManager.signoutRedirect({
        id_token_hint: idToken,
        post_logout_redirect_uri:
          process.env.REACT_APP_IDENTITY_CENTER_POST_LOGOUT_REDIRECT_URI ||
          window.location.origin,
      }).catch((error) => {
        console.error('[AuthContext] Erro no signoutRedirect:', error);
        setLoggingOut(false);
        window.location.href = '/';
      });
    }).catch((error) => {
      console.error('[AuthContext] Erro ao remover usuário:', error);
      setLoggingOut(false);
      window.location.href = '/';
    });
  }, [user]);

  const getAccessToken = useCallback(() => {
    return user?.access_token || null;
  }, [user]);

  // ---------------------------------------------------------------------------
  // Context value
  // ---------------------------------------------------------------------------

  const isAuthenticated = !!user && !user.expired;

  const value = {
    user,
    loading,
    isAuthenticated,
    accessDenied,
    loggingOut,
    login,
    logout,
    getAccessToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Hook para consumir o AuthContext.
 * Deve ser usado dentro de um componente filho de <AuthProvider>.
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um <AuthProvider>');
  }
  return context;
}
